import React from 'react';
import { SparklesStarIcon } from './DepartmentIcons';

interface DepartmentOverviewViewProps {
  summary: any;
  onNavigateView: (viewKey: string) => void;
  onOpenSuperAi: () => void;
}

export const DepartmentOverviewView: React.FC<DepartmentOverviewViewProps> = ({
  summary,
  onNavigateView,
  onOpenSuperAi,
}) => {
  const kpis = summary?.kpis || {
    doctoral_count: 12,
    staff_count: 6,
    risk_count: 4,
    avg_progress: 74,
  };

  const commandCards = [
    {
      num: '01',
      title: 'Мониторинг докторантов',
      desc: 'Курсы, программы, руководители, НИРД, прогресс и защита — без выхода из панели.',
      view: 'monitoring',
    },
    {
      num: '02',
      title: 'Сотрудники и нагрузка',
      desc: 'Состав кафедры, научное руководство, рабочие контуры и ответственные.',
      view: 'staff',
    },
    {
      num: '03',
      title: 'Документы на контроле',
      desc: 'Версии файлов, комплектность, замена и единый кафедральный реестр.',
      view: 'documents',
    },
    {
      num: '04',
      title: 'Встречи и дежурства',
      desc: 'События, приглашения, подтверждения, ответственные и ближайшие сроки.',
      view: 'events',
    },
  ];

  const controlItems = summary?.control_today || [
    { id: '1', name: 'Ильяс Ахмет', course: 3, nird_status: 'Не сдан', progress: 64, accent_color: '#ef4444' },
    { id: '2', name: 'Алина Бекмуратова', course: 1, nird_status: 'Черновик', progress: 58, accent_color: '#f59e0b' },
    { id: '3', name: 'Дана Төлеген', course: 1, nird_status: 'Черновик', progress: 61, accent_color: '#f59e0b' },
    { id: '4', name: 'Нұржан Асан', course: 1, nird_status: 'Не сдан', progress: 49, accent_color: '#ef4444' },
  ];

  const recentTasks = summary?.recent_tasks || [
    { id: 't1', title: 'Предоставить список докторантов 1 курса', recipient: 'Эдвайзер', deadline: 'до 18.08.2026', status: 'IN_PROGRESS' },
    { id: 't2', title: 'Уточнить места работы докторантов', recipient: 'Докторанты 1–3 курса', deadline: 'до 19.08.2026', status: 'WAITING' },
    { id: 't3', title: 'Сводка по НИРД за семестр', recipient: 'Эдвайзер', deadline: 'до 20.08.2026', status: 'NEW' },
  ];

  const recentSurveys = summary?.recent_surveys || [
    { id: 's1', title: 'Актуальное место работы докторантов', subtitle: '9/12 ответов · до 18.08.2026', progress: 75 },
    { id: 's2', title: 'Участие в мероприятиях кафедры', subtitle: '7/12 ответов · до 21.08.2026', progress: 58 },
  ];

  const recentDocs = summary?.recent_docs || [
    { id: 'd1', filename: 'Список докторантов 2026–2027.xlsx', version: 'v4' },
    { id: 'd2', filename: 'Контроль НИРД.xlsx', version: 'v5' },
  ];

  const recentEvents = summary?.recent_events || [
    { id: 'e1', title: 'Заседание кафедры', status: 'PLANNED' },
    { id: 'e2', title: 'Дежурство по кафедре', status: 'CONFIRMED' },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-6">
        <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
          Department Control
        </span>
        <h2 className="text-2xl font-bold text-white tracking-tight mt-1">
          Панель кафедры · {summary?.role_title || 'Руководитель подразделения'}
        </h2>
        <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-4xl">
          Полноэкранный кафедральный центр: контроль докторантуры, сотрудники и нагрузка, поручения, быстрые запросы, документы, аналитика, встречи и дежурства. Оба кабинета работают одинаково.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Докторанты</span>
            <div className="text-3xl font-extrabold text-white mt-1">{kpis.doctoral_count}</div>
            <span className="text-xs text-cyan-400 mt-1 block">1–3 курс</span>
          </div>

          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Сотрудники</span>
            <div className="text-3xl font-extrabold text-white mt-1">{kpis.staff_count}</div>
            <span className="text-xs text-slate-400 mt-1 block">рабочая выборка</span>
          </div>

          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Контрольные риски</span>
            <div className="text-3xl font-extrabold text-rose-400 mt-1">{kpis.risk_count}</div>
            <span className="text-xs text-rose-300 mt-1 block">НИРД / прогресс</span>
          </div>

          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Средний прогресс</span>
            <div className="text-3xl font-extrabold text-white mt-1">{kpis.avg_progress}%</div>
            <span className="text-xs text-cyan-400 mt-1 block">по досье</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {commandCards.map((cmd) => (
          <button
            key={cmd.num}
            type="button"
            onClick={() => onNavigateView(cmd.view)}
            className="group p-5 rounded-2xl border border-cyan-500/20 bg-[#0a1c2c] hover:bg-[#0e273e] hover:border-cyan-400/40 text-left transition-all shadow-md active:scale-[0.98]"
          >
            <span className="inline-block text-[10px] font-mono font-bold text-cyan-400 px-2 py-0.5 rounded bg-[#061420] border border-cyan-500/30 mb-2">
              {cmd.num}
            </span>
            <h3 className="text-sm font-bold text-white group-hover:text-cyan-300 transition-colors">
              {cmd.title}
            </h3>
            <p className="text-xs text-slate-400 mt-1 line-clamp-2">
              {cmd.desc}
            </p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <div className="mb-4">
            <h3 className="text-base font-bold text-white">Контроль сегодня</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Автоматическая рабочая сводка по проблемным точкам. Клик по выборке открывает тот же кафедральный контур.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {controlItems.map((item: any) => (
              <div
                key={item.id}
                onClick={() => onNavigateView('monitoring')}
                className="cursor-pointer p-3.5 rounded-xl border border-slate-700/60 bg-[#061420] hover:bg-[#081d2f] transition-all relative overflow-hidden"
              >
                <div
                  className="absolute left-0 top-0 bottom-0 w-1"
                  style={{ backgroundColor: item.accent_color || '#f59e0b' }}
                />
                <div className="pl-2">
                  <h4 className="text-sm font-bold text-white">{item.name}</h4>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {item.course} курс · {item.nird_status} · прогресс {item.progress}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-cyan-950/50 border border-cyan-500/30 text-cyan-300">
                <SparklesStarIcon className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-white">AI-сводка кафедры</h3>
            </div>
            <p className="text-xs text-slate-300 mt-3 leading-relaxed">
              {summary?.ai_summary ||
                'Найдено 4 контрольных записей. 3 докторанта имеют зафиксированную защиту. Ближайшая управленческая задача — закрыть НИРД-риски и проверить даты защиты выпускного курса.'}
            </p>
          </div>

          <button
            type="button"
            onClick={onOpenSuperAi}
            className="mt-4 w-full py-2.5 px-4 rounded-xl border border-cyan-500/30 bg-[#0c2336] hover:bg-[#112f48] text-cyan-300 hover:text-cyan-200 text-xs font-semibold transition-all text-center"
          >
            Открыть аналитику
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-white">Поручения</h3>
              <p className="text-xs text-slate-400">Активные служебные задачи.</p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateView('tasks')}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-cyan-500/30 bg-[#0c2336] hover:bg-[#102e46] text-cyan-300"
            >
              Открыть
            </button>
          </div>

          <div className="space-y-2">
            {recentTasks.map((t: any) => (
              <div
                key={t.id}
                className="flex items-center justify-between p-3 rounded-xl bg-[#061420] border border-slate-800"
              >
                <div className="min-w-0 pr-2">
                  <p className="text-xs font-semibold text-white truncate">{t.title}</p>
                  <p className="text-[11px] text-slate-400">{t.recipient} · {t.deadline}</p>
                </div>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold ${
                  t.status === 'IN_PROGRESS'
                    ? 'bg-cyan-950 text-cyan-300 border border-cyan-800'
                    : t.status === 'WAITING'
                    ? 'bg-amber-950 text-amber-300 border border-amber-800'
                    : 'bg-slate-800 text-slate-300 border border-slate-700'
                }`}>
                  {t.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-white">Опросы и запросы</h3>
              <p className="text-xs text-slate-400">Актуализация сведений без внешних форм.</p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateView('surveys')}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-cyan-500/30 bg-[#0c2336] hover:bg-[#102e46] text-cyan-300"
            >
              Открыть
            </button>
          </div>

          <div className="space-y-2">
            {recentSurveys.map((s: any) => (
              <div
                key={s.id}
                className="flex items-center justify-between p-3 rounded-xl bg-[#061420] border border-slate-800"
              >
                <div className="min-w-0 pr-2">
                  <p className="text-xs font-semibold text-white truncate">{s.title}</p>
                  <p className="text-[11px] text-slate-400">{s.subtitle || `${s.responses_count}/${s.total_target} ответов`}</p>
                </div>
                <span className="text-[11px] font-mono font-bold text-cyan-400">
                  {s.progress || (s.total_target ? Math.round((s.responses_count / s.total_target) * 100) : 0)}%
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-white">Последние документы</h3>
              <p className="text-xs text-slate-400">Версия и дата обновления.</p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateView('documents')}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-cyan-500/30 bg-[#0c2336] hover:bg-[#102e46] text-cyan-300"
            >
              Открыть
            </button>
          </div>

          <div className="space-y-2">
            {recentDocs.map((d: any) => (
              <div
                key={d.id}
                className="flex items-center justify-between p-3 rounded-xl bg-[#061420] border border-slate-800"
              >
                <p className="text-xs font-semibold text-white truncate pr-2">{d.filename}</p>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded font-bold bg-cyan-950 text-cyan-300 border border-cyan-800">
                  {d.version}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-white">Ближайшие события</h3>
              <p className="text-xs text-slate-400">Встречи и дежурства.</p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateView('events')}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-cyan-500/30 bg-[#0c2336] hover:bg-[#102e46] text-cyan-300"
            >
              Открыть
            </button>
          </div>

          <div className="space-y-2">
            {recentEvents.map((e: any) => (
              <div
                key={e.id}
                className="flex items-center justify-between p-3 rounded-xl bg-[#061420] border border-slate-800"
              >
                <p className="text-xs font-semibold text-white truncate pr-2">{e.title}</p>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold ${
                  e.status === 'CONFIRMED'
                    ? 'bg-emerald-950 text-emerald-300 border border-emerald-800'
                    : 'bg-amber-950 text-amber-300 border border-amber-800'
                }`}>
                  {e.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
